import { gql } from '@apollo/client';
import { AppPatient, PriorAuthData, PriorAuthQueryPayload } from '../types';
import { createLogger } from '../utils/createLogger';
import { filterLogContext } from '../utils/filterLogContext';
import { graphQlClient } from '../utils/graphQlClient';
import { checkPriorAuthGQLQuery } from '@getvim/vim-connect/src/infra/common/api/patient/priorAuth';
import { filterUnsupportedDiagnosisCodeValues } from '../utils/filterUnsupportedDiagnosisCodeValues';

const logger = createLogger('checkPriorAuthQuery');

export interface CheckPriorAuthInput {
  anonymizedPatient?: string;
  encryptedPatientDetails?: {
    encryptedIdentifyInput?: string;
  };
  procedureCodes: string[];
  diagnosisCode?: string;
  vimPatientId?: string;
}

const CHECK_PRIOR_AUTH_QUERY_GQL = gql`
  query CheckPriorAuthorization($input: CheckPriorAuthInput!) {
    checkPriorAuthorization(input: $input) {
      procedures {
        code
        description
        decisionSummary
        sitesOfService {
          name
          decision
          code
          conditionsList
        }
      }
      productType
      state
      disclaimer
    }
  }
`;

const checkPriorAuthQueryGQL = async (input: CheckPriorAuthInput, insurer?: string) => {
  try {
    const gqlClient = graphQlClient.getClient();
    const { data } = await gqlClient.query<{ checkPriorAuthorization: PriorAuthData }>({
      query: CHECK_PRIOR_AUTH_QUERY_GQL,
      variables: { input },
      fetchPolicy: 'no-cache',
      context: {
        uri: '/prior-auth/api/graphql',
        ...(insurer && {
          headers: {
            'x-insurer': insurer,
          },
        }),
      },
    });
    return { data: data.checkPriorAuthorization, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export async function checkPriorAuthQuery(payload: PriorAuthQueryPayload, patient: AppPatient) {
  const logContext = filterLogContext({ patient });
  logger.info('Start checkPriorAuthQuery', {
    payload,
    ...logContext,
    noPHI: true,
  });
  const { procedures, diagnosis } = payload;
  if (!patient?.patientSourceVaultToken) {
    logger.warning('Patient token is required for check prior auth query!', {
      ...logContext,
      noPHI: true,
    });
    return {
      error: 'Patient token not found',
    };
  }
  const diagnosisCode = filterUnsupportedDiagnosisCodeValues(diagnosis);
  let procedureCodes = procedures.map((procedure) => procedure.value);
  if (procedureCodes?.length > 5) {
    const reduced = procedureCodes.slice(0, 5);
    logger.info('reducing procedure codes array to 5 items (POCA API maximum)', {
      original: procedureCodes,
      reduced,
      ...logContext,
      noPHI: true,
    });
    procedureCodes = reduced;
  }

  const result = await checkPriorAuthQueryGQL(
    {
      ...(diagnosisCode && { diagnosisCode }),
      procedureCodes,
      vimPatientId: patient?.identifiers?.vimPatientId,
      anonymizedPatient: patient.patientSourceVaultToken,
    },
    payload.insurer,
  );
  const { data, error } = result ?? {};
  if (error) {
    logger.error('Error checking prior auth query', {
      ...logContext,
      noPHI: true,
      error,
    });
    return {
      error: (error as any)?.message,
    };
  }

  return {
    priorAuthData: {
      ...data,
      ...(diagnosis && { diagnosis: { code: diagnosis?.value, description: diagnosis?.label } }),
    },
  };
}
